import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storyAPI } from '../../services/storyApi';

// Async thunks
export const fetchStories = createAsyncThunk(
  'stories/fetchStories',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await storyAPI.getStories(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch stories');
    }
  }
);

export const createStory = createAsyncThunk(
  'stories/createStory',
  async (data, { rejectWithValue }) => {
    try {
      const response = await storyAPI.createStory(data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create story');
    }
  }
);

export const deleteStory = createAsyncThunk(
  'stories/deleteStory',
  async (id, { rejectWithValue }) => {
    try {
      await storyAPI.deleteStory(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete story');
    }
  }
);

export const reactToStory = createAsyncThunk(
  'stories/reactToStory',
  async ({ id, reactionType }, { rejectWithValue }) => {
    try {
      const response = await storyAPI.reactToStory(id, reactionType);
      return { id, reaction: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to react to story');
    }
  }
);

export const removeReaction = createAsyncThunk(
  'stories/removeReaction',
  async (id, { rejectWithValue }) => {
    try {
      await storyAPI.removeReaction(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove reaction');
    }
  }
);

export const recordView = createAsyncThunk(
  'stories/recordView',
  async (id, { rejectWithValue }) => {
    try {
      await storyAPI.recordView(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to record view');
    }
  }
);

export const replyToStory = createAsyncThunk(
  'stories/replyToStory',
  async ({ id, message }, { rejectWithValue }) => {
    try {
      const response = await storyAPI.replyToStory(id, message);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reply to story');
    }
  }
);


const storiesSlice = createSlice({
  name: 'stories',
  initialState: {
    items: [],
    myStories: [],
    currentStory: null,
    loading: false,
    creating: false,
    error: null,
    hasMore: true,
    currentPage: 1,
  },
  reducers: {
    clearStoriesError: (state) => {
      state.error = null;
    },
    setCurrentStory: (state, action) => {
      state.currentStory = action.payload;
    },
    clearCurrentStory: (state) => {
      state.currentStory = null;
    },
    addStory: (state, action) => {
      state.items.unshift(action.payload);
      state.myStories.unshift(action.payload);
    },
    updateStoryStats: (state, action) => {
      const { id, views_count, reactions_count } = action.payload;
      const story = state.items.find(s => s.id === id);
      if (story) {
        story.views_count = views_count;
        story.reactions_count = reactions_count;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch stories
      .addCase(fetchStories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStories.fulfilled, (state, action) => {
        state.loading = false;
        const page = action.meta.arg?.page || 1;
        if (page === 1) {
          state.items = action.payload.data;
        } else {
          state.items = [...state.items, ...action.payload.data];
        }
        state.hasMore = action.payload.data.length === 20;
        state.currentPage = page;
      })

      .addCase(fetchStories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create story
      .addCase(createStory.pending, (state) => {
        state.creating = true;
        state.error = null;
      })
      .addCase(createStory.fulfilled, (state, action) => {
        state.creating = false;
        state.items.unshift(action.payload.data);
        state.myStories.unshift(action.payload.data);
      })
      .addCase(createStory.rejected, (state, action) => {
        state.creating = false;
        state.error = action.payload;
      })
      // Delete story
      .addCase(deleteStory.fulfilled, (state, action) => {
        state.items = state.items.filter(s => s.id !== action.payload);
        state.myStories = state.myStories.filter(s => s.id !== action.payload);
      })
      // React to story
      .addCase(reactToStory.fulfilled, (state, action) => {
        const { id, reaction } = action.payload;
        const story = state.items.find(s => s.id === id);
        if (story) {
          story.user_reaction = reaction.data;
          story.reactions_count = (story.reactions_count || 0) + 1;
        }
      })
      // Remove reaction
      .addCase(removeReaction.fulfilled, (state, action) => {
        const story = state.items.find(s => s.id === action.payload);
        if (story) {
          story.user_reaction = null;
          story.reactions_count = Math.max(0, (story.reactions_count || 0) - 1);
        }
      })
      // Record view
      .addCase(recordView.fulfilled, (state, action) => {
        const story = state.items.find(s => s.id === action.payload);
        if (story) {
          story.views_count = (story.views_count || 0) + 1;
          story.has_viewed = true;
        }
      });
  },
});

export const {
  clearStoriesError,
  setCurrentStory,
  clearCurrentStory,
  addStory,
  updateStoryStats,
} = storiesSlice.actions;

export default storiesSlice.reducer;
